import {Area, AreaWithSensorInfo, NodeInformation} from '@lib/store';
import {
  UntraceableMarkerData,
  BookableDeskSensorAvailabilityMarkerData,
  BookableDeskSensorMarkerData,
  SensorAvailabilityMarkerData,
  SensorMarkerData,
} from '../../../../submodules/map/mapiq-map/markers/NodeMarkerData';
import {SensorAvailabilityStatus, WorkspaceOccupancyState} from '@lib/infrastructure';

type AreaLike = Pick<Area, 'id' | 'capacity' | 'adHocCapacity'> & {
  workspaceBookingPolicyType?: AreaWithSensorInfo['workspaceBookingPolicyType'];
};
export const createSensorHighlightsForAreas = (
  areas: AreaLike[],
  occupancyStates: WorkspaceOccupancyState[],
  connectionReservations: {
    deskId: string;
    connection: {
      initials: string;
      color: string;
      picture: string | null;
    };
  }[] = [],
  isThreeDMapsEnabled = false,
  isVariantB = false,
  areaBookingStates: {
    [areaId: string]: NodeInformation | undefined;
  } = {},
):
  | (BookableDeskSensorMarkerData | SensorMarkerData | UntraceableMarkerData)[]
  | (BookableDeskSensorAvailabilityMarkerData | SensorAvailabilityMarkerData | UntraceableMarkerData)[]
  | UntraceableMarkerData[] => {
  const occupancyByNodeId = new Map(occupancyStates.map((s) => [s.node.id, s]));
  const bookingsByAreaId = new Map(Object.entries(areaBookingStates).map(([areaId, state]) => [areaId, state]));
  const connectionsByDeskId = new Map(connectionReservations.map((c) => [c.deskId, c]));

  if (isThreeDMapsEnabled) {
    return createMarkers<SensorAvailabilityMarkerData, BookableDeskSensorAvailabilityMarkerData>(
      areas,
      occupancyByNodeId,
      connectionsByDeskId,
      {
        sensorType: 'sensor_availability',
        bookableDeskSensorType: 'bookable_desk_sensor_availability',
      },
      bookingsByAreaId,
    ).filter((m) => {
      if (isVariantB || m.type === 'bookable_desk_sensor_availability' || m.type === 'untraceable') return true;

      // Sensor availability markers with `occupied` state remain hidden
      return m.nrPresent === 0;
    });
  }

  return createMarkers<SensorMarkerData, BookableDeskSensorMarkerData>(areas, occupancyByNodeId, connectionsByDeskId, {
    sensorType: 'sensor',
    bookableDeskSensorType: 'bookable_desk_sensor',
  });
};

type MarkerBase = {
  capacity: number;
  nodeId: string;
  nrPresent: number;
  nrTentative: number;
  nrUnknown: number;
};

type ConnectionData = {
  initials: string;
  color: string;
  picture: string | null;
};

function createMarkers<
  Sensor extends MarkerBase & {
    type: string;
    nrAvailable?: number;
  },
  Bookable extends MarkerBase & {
    type: string;
    booked: boolean;
    connection: ConnectionData | null;
    nrAvailable?: number;
  },
>(
  areas: AreaLike[],
  occupancyByNodeId: Map<string, WorkspaceOccupancyState>,
  connectionsByDeskId: Map<string, {connection: ConnectionData}>,
  config: {
    sensorType: Sensor['type'];
    bookableDeskSensorType: Bookable['type'];
  },
  bookingsByAreaId?: Map<string, NodeInformation | undefined>,
): (Sensor | Bookable | UntraceableMarkerData)[] {
  return areas.flatMap(
    (area): Sensor | Bookable | UntraceableMarkerData | (Sensor | Bookable | UntraceableMarkerData)[] => {
      const state = occupancyByNodeId.get(area.id);
      const capacity = area.capacity || area.adHocCapacity;

      // If the area does not have a sensor state, we give it an unknown sensor
      if (!state) {
        const booking = bookingsByAreaId?.get(area.id);
        const nrPresent = booking?.occupation ?? 0;
        const nrAvailable = capacity - nrPresent;

        if (booking?.occupation || area.workspaceBookingPolicyType !== 'NonBookable') {
          return {
            capacity,
            nodeId: area.id,
            type: config.sensorType,
            nrPresent,
            nrTentative: 0,
            nrUnknown: booking ? 0 : capacity,
            ...(config.sensorType === 'sensor_availability' && {
              nrAvailable,
            }),
          } as Sensor;
        }

        return {
          type: 'untraceable',
          subType: 'workspace',
          nodeId: area.id,
          capacity: 1,
        };
      }

      if (state.desks.length === 0) {
        const {nrPresent, nrTentative, nrUnknown} = state.workplaceState;
        const nrAvailable = capacity - (nrPresent + nrTentative + nrUnknown);

        return {
          capacity,
          nodeId: area.id,
          type: config.sensorType,
          nrPresent,
          nrTentative,
          nrUnknown,
          ...(config.sensorType === 'sensor_availability' && {
            nrAvailable,
          }),
        } as Sensor;
      }

      return state.desks.map((desk): Sensor | Bookable => {
        const base = {
          capacity: 1,
          nodeId: desk.id,
          nrPresent: desk.state === SensorAvailabilityStatus.Occupied ? 1 : 0,
          nrTentative: desk.state === SensorAvailabilityStatus.Away ? 1 : 0,
          nrUnknown: desk.state === SensorAvailabilityStatus.Unknown ? 1 : 0,
          ...(config.sensorType === 'sensor_availability' && {
            nrAvailable: desk.state === SensorAvailabilityStatus.Available ? 1 : 0,
          }),
        };

        const connectionReservation = connectionsByDeskId.get(desk.id);
        if (connectionReservation) {
          const isAvailableAccordingToSensor = desk.state === SensorAvailabilityStatus.Available;

          return {
            ...base,
            type: config.bookableDeskSensorType,
            booked: true,
            connection: connectionReservation.connection,
            // When we know the desk is booked, can can override the state
            nrUnknown: isAvailableAccordingToSensor ? 1 : base.nrUnknown,
            // Booked desks are not available
            ...(config.sensorType === 'sensor_availability' && {
              nrAvailable: 0,
            }),
          } as Bookable;
        }

        return {
          ...base,
          type: config.sensorType,
        } as Sensor;
      });
    },
  );
}
